import isEmpty from 'lodash/isEmpty';
import { IMSTMap } from 'mobx-state-tree';
import { RegionMap } from 'modules/Canvas/components/CanvasRegionList/CanvasRegionList';
import { stringToColor } from 'utils/strings/strings';
import { hasFrames, hasFramesWithRegions, hasRegionsWithConcepts } from 'components/ModelOutputRenderer/components/utils';
import { uniqBy } from 'lodash';
import { ImageElementData } from 'components/Canvas/RegionThumb/common';

export const buildRegionMap = (
  dataArray: (CF.API.TOutputData & { id?: string })[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  modelOutputSettings?: IMSTMap<any>,
  withScore: boolean = true,
) => {
  const filteredOutputData = dataArray.filter((outputData) => hasRegionsWithConcepts(outputData) || hasFrames(outputData)) as ((
    | CF.API.BaseRegionsData
    | CF.API.BaseFramesData
  ) & { id?: string })[];

  const uniqOutputData = uniqBy(filteredOutputData, (outputData) => {
    if (hasRegionsWithConcepts(outputData)) {
      return outputData.regions?.[0].id;
    }

    return outputData.frames?.[0].id;
  });

  return uniqOutputData.reduce((regionMap, data) => {
    const modelId = data.id;
    const regions = hasFramesWithRegions(data) ? mapFramesToRegions(data) || [] : data.regions || [];

    for (const region of regions) {
      if (!isEmpty(region.data)) {
        const concepts = region.data.concepts;
        const text = region.data.text?.raw;

        if (concepts?.length) {
          const score = concepts[0].value;
          const scoreTxt = score === 1 || score === 0 ? score.toFixed(1) : score?.toString().slice(0, 5) || '';
          const concept = {
            id: concepts[0].id,
            name: concepts[0].name,
            value: withScore ? scoreTxt : undefined,
            color: stringToColor(concepts[0].id),
          };

          const minThreshold = (modelId && modelOutputSettings?.get(modelId)?.minThreshold) || 0;

          if (!modelOutputSettings || (score || 0) > minThreshold) {
            // Display concept label only if value above min threshold
            if (region.id in regionMap) {
              regionMap[region.id].push(concept);
            } else {
              regionMap[region.id] = [concept];
            }
          }
        }

        if (text) {
          const textObj = {
            name: text,
            value: undefined,
          };

          if (region.id in regionMap) {
            const existingTextObj = regionMap[region.id][0];

            regionMap[region.id] = [{ ...existingTextObj, value: text }];
          } else {
            regionMap[region.id] = [textObj];
          }
        }
      }
    }

    return regionMap;
  }, {} as RegionMap);
};

const mapFramesToRegions = (data: CF.API.BaseFramesData) => data.frames?.flatMap((frame) => (frame.data as CF.API.BaseRegionsData).regions || []);

export const getImageDataFromImage = (image?: HTMLImageElement) => {
  if (!image) {
    return null;
  }

  return {
    image,
    width: image.width,
    height: image.height,
    url: image.src,
  } as ImageElementData;
};
